import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2, SortAsc, SortDesc } from "lucide-react";

const TableSkeleton = () => {
    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead className="cursor-pointer">
                        Date/Time
                    </TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Primary Analysis</TableHead>
                    <TableHead>Secondary Analysis</TableHead>
                    <TableHead className="cursor-pointer">
                        Websites
                    </TableHead>
                    <TableHead className="cursor-pointer">
                        Matches
                    </TableHead>
                    <TableHead className="cursor-pointer">
                        Errors
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {[...Array(10)].map((_, index) => (
                    <TableRow key={index}>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-32"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-[150px]"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-[200px]"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-[200px]"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
                        </TableCell>
                        <TableCell>
                            <div className="h-4 bg-gray-200 rounded animate-pulse w-12"></div>
                        </TableCell>
                        <TableCell>
                            <div className="px-2 py-1 rounded-full w-20 h-6 bg-gray-200 animate-pulse"></div>
                        </TableCell>
                        <TableCell>
                            <div className="flex items-center gap-2">
                                <div className="h-8 w-8 bg-gray-200 rounded animate-pulse"></div>
                            </div>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const AnalysisTable = ({ analyses, sortConfig, handleSort, handleDownload, downloadingId, loading }) => {
    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '-';
        if (text.length <= maxLength) return text;
        return `${text.substring(0, maxLength)}...`;
    };

    const renderMetric = (value, isCompleted) => {
        if (!isCompleted) {
            return (
                <div className="flex">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                </div>
            );
        }
        return value;
    };

    if (loading) {
        return <TableSkeleton />;
    }

    return (
        <Table>
            <TableHeader>
                <TableRow>
                    <TableHead onClick={() => handleSort('timestamp')} className="cursor-pointer">
                        Date/Time
                        {sortConfig.column === 'timestamp' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="inline ml-1 w-4 h-4" /> : <SortDesc className="inline ml-1 w-4 h-4" />
                        )}
                    </TableHead>
                    <TableHead onClick={() => handleSort('analysis_name')} className="cursor-pointer">
                        Name
                        {sortConfig.column === 'analysis_name' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="inline ml-1 w-4 h-4" /> : <SortDesc className="inline ml-1 w-4 h-4" />
                        )}
                    </TableHead>
                    <TableHead>Primary Analysis</TableHead>
                    <TableHead>Secondary Analysis</TableHead>
                    <TableHead onClick={() => handleSort('websites_analyzed')} className="cursor-pointer">
                        Websites
                        {sortConfig.column === 'websites_analyzed' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="inline ml-1 w-4 h-4" /> : <SortDesc className="inline ml-1 w-4 h-4" />
                        )}
                    </TableHead>
                    <TableHead onClick={() => handleSort('matches_found')} className="cursor-pointer">
                        Matches
                        {sortConfig.column === 'matches_found' && (
                            sortConfig.direction === 'asc' ? <SortAsc className="inline ml-1 w-4 h-4" /> : <SortDesc className="inline ml-1 w-4 h-4" />
                        )}
                    </TableHead>
                    <TableHead onClick={() => handleSort('errors_count')} className="cursor-pointer">
                        <TooltipProvider>
                            <Tooltip>
                                <TooltipTrigger className="flex items-center gap-1">
                                    <span>Errors</span>
                                    {sortConfig.column === 'errors_count' && (
                                        sortConfig.direction === 'asc' ? <SortAsc className="w-4 h-4" /> : <SortDesc className="w-4 h-4" />
                                    )}
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Websites that couldn't be accessed. Credits for these have been refunded.</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {analyses.map((analysis) => {
                    const isCompleted = analysis.status === 'completed';
                    return (
                        <TableRow key={analysis.id}>
                            <TableCell>{formatDate(analysis.timestamp)}</TableCell>
                            <TableCell>
                                <span className="block truncate max-w-[150px]" title={analysis.analysis_name}>
                                    {truncateText(analysis.analysis_name, 20)}
                                </span>
                            </TableCell>
                            <TableCell>
                                <span className="block truncate max-w-[200px]" title={analysis.primary_analysis_type_name || analysis.primary_analysis_type}>
                                    {analysis.primary_analysis_type_name || analysis.primary_analysis_type}
                                </span>
                            </TableCell>
                            <TableCell>
                                <span className="block truncate max-w-[200px]" title={analysis.secondary_analysis_type_name || analysis.secondary_analysis_type || '-'}>
                                    {analysis.secondary_analysis_type_name || analysis.secondary_analysis_type || '-'}
                                </span>
                            </TableCell>
                            <TableCell>{analysis.websites_analyzed}</TableCell>
                            <TableCell>{renderMetric(analysis.matches_found, isCompleted)}</TableCell>
                            <TableCell>{renderMetric(analysis.errors_count, isCompleted)}</TableCell>
                            <TableCell className="py-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    analysis.status === 'failed' ? 'bg-red-100 text-red-800' :
                                        'bg-yellow-100 text-yellow-800'
                                    }`}>
                                    {analysis.status.charAt(0).toUpperCase() + analysis.status.slice(1)}
                                </span>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    {analysis.csv_result_url && (
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleDownload(analysis)}
                                            disabled={downloadingId === analysis.id}
                                            title="Download Results"
                                        >
                                            {downloadingId === analysis.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <FileDown className="h-4 w-4" />
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    );
};

export default AnalysisTable;